import { useCallback, useState, useEffect } from 'react';
import fetch from '@rd-web-markets/shared/dist/services/Fetcher';
import { request } from '@rd-web-markets/shared/dist/services/request';
import { useErrorHandling } from '@rd-web-markets/shared/dist/hooks/useErrorHandling';
import serviceMethods from './service_methods';
import { buildService } from './service';
const api = buildService('/users', {
  only: ['all', 'create', 'update', 'delete']
});
export const UserService = {
  useFetchUsersForSelect: function () {
    let type = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
    let extraParams = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const [users, setUsers] = useState([]);
    const fetchUsers = useErrorHandling(useCallback(async () => {
      const response = await UserService.all(type, extraParams);
      setUsers(response.users.map(user => ({
        value: user.id,
        label: user.name
      })));
    }, [type, JSON.stringify(extraParams)]));
    useEffect(() => {
      fetchUsers();
    }, [fetchUsers]);
    return [users, setUsers];
  },
  all: function (type) {
    let extraParams = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const query = new URLSearchParams({
      type,
      ...extraParams
    }).toString();
    return fetch(`/api/users/?${query}`, request.get);
  },
  get: id => {
    return fetch(`/api/users/${id}`, request.get);
  },
  getAuthorizationPath: () => {
    return fetch('/api/users/authorize_path', request.get);
  },
  initiateAuthorization: (authorizationPath, authenticityToken) => {
    return fetch(authorizationPath, {
      ...request.post,
      body: JSON.stringify({
        authenticity_token: authenticityToken
      })
    }).then(data => {
      console.log(data);
    });
  },
  logout: () => {
    return fetch('/api/users/sign_out.json', request.post);
  },
  delete: id => {
    return fetch(`/api/users/${id}`, request.delete);
  },
  update: user => {
    return fetch(`/api/users/${user.id}`, {
      ...request.put,
      body: JSON.stringify({
        user
      })
    });
  },
  create: user => {
    return fetch('/api/users/', {
      ...request.post,
      body: JSON.stringify({
        user
      })
    });
  },
  confirm: user => {
    return fetch('/api/users/confirm.json', {
      ...request.post,
      body: JSON.stringify({
        user
      })
    });
  },
  getCurrent: function () {
    return fetch('/api/users/current_session.json', request.get);
  },
  generateOtpBackupCodes: function () {
    return fetch('/api/users/otp_backup_codes.json', request.post);
  },
  validateConfirmationToken: token => {
    return fetch(`/api/users/confirm.json?user[confirmation_token]=${token}`, request.get);
  },
  sendInviteEmail: userId => {
    return fetch(`/api/users/send_invite_email/${userId}.json`, request.post);
  },
  updatePassword: user => {
    return fetch('/api/users/password.json', {
      ...request.put,
      body: JSON.stringify({
        user
      })
    });
  },
  sendPasswordResetInstructions: user => {
    return fetch('/api/users/password', {
      method: 'POST',
      body: JSON.stringify({
        user
      })
    });
  },
  signIn: user => {
    return fetch('/api/users/sign_in', {
      method: 'POST',
      body: JSON.stringify({
        user
      })
    });
  },
  useFetch(_ref) {
    let {
      setLoading,
      fetchImediately = true
    } = _ref;
    const [users, setUsers, fetchUsers] = serviceMethods.useFetch({
      callback: useCallback(async queryParams => await api.all(queryParams), []),
      setLoading,
      fetchImediately
    });
    return [users, setUsers, fetchUsers];
  },
  useCreate(_ref2) {
    let {
      onSuccess,
      onError,
      onFinally,
      setLoading
    } = _ref2;
    const createModel = serviceMethods.useCreate({
      setLoading,
      onSuccess,
      onError,
      onFinally,
      callback: useCallback(async user => {
        await api.create(user);
      }, [])
    });
    return createModel;
  },
  useUpdate(_ref3) {
    let {
      onSuccess,
      onError,
      onFinally,
      setLoading
    } = _ref3;
    const update = serviceMethods.useUpdate({
      setLoading,
      onSuccess,
      onError,
      onFinally,
      callback: useCallback(async user => {
        if (!user) {
          return null;
        }
        return await api.update(user.id, {
          user
        });
      }, [])
    });
    return update;
  }
};